import { Project, ProjectMapTemplate } from '@beeboard/common';
import { ComposerTranslation } from 'vue-i18n';
import writeXlsxFile, { SheetData } from 'write-excel-file';
import { useProjectStore, useTemplateStore } from '@/store';

export async function exportExcelFile(
  project: Project,
  $t: ComposerTranslation,
  containsDocuments = false,
) {
  const rows: SheetData = [
    [
      {
        value: $t('glossary.number'),
        type: String,
      },
      {
        value: $t('task.type'),
        type: String,
      },
      {
        value: $t('activity.title'),
        type: String,
      },
      {
        value: $t('activity.plannedDueDate'),
        type: String,
      },
      {
        value: $t('activity.state'),
        type: String,
      },
      {
        value: $t('activity.status'),
        type: String,
      },
      {
        value: $t('activity.responsible'),
        type: String,
      },
      {
        value: $t('function.function'),
        type: String,
      },
      {
        value: $t('glossary.workstream'),
        type: String,
      },
      {
        value: $t('glossary.phase'),
        type: String,
      },
      {
        value: containsDocuments ? $t('glossary.url') : undefined,
        type: String,
      },
    ],
  ];
  project.activities
    .sort((a, b) => (a.current_deadline < b.current_deadline ? -1 : 1))
    .forEach((activity) => {
      const stateKey = `statusBadges.${activity.state}`;
      const statusKey = `statusColors.${activity.status}`;
      rows.push([
        {
          value: activity.number.toString(),
          type: String,
        },
        {
          value: $t('activity.abbreviation'),
          type: String,
        },
        {
          value: activity.title,
          type: String,
        },
        {
          value: activity.current_deadline.replace('T00:00Z', ''),
          type: String,
        },
        {
          value: $t(stateKey),
          type: String,
        },
        {
          value: activity.status ? $t(statusKey) : undefined,
          type: String,
        },
        {
          value: activity.responsible.user?.email || undefined,
          type: String,
        },
        {
          value:
            activity.responsible.function_id !== null
              ? useProjectStore().projectFunction(
                  project.id,
                  activity.responsible.function_id,
                ).name
              : undefined,
          type: String,
        },
        {
          value: useProjectStore().workstream(project.id, activity.workstream)
            .title,
          type: String,
        },
        {
          value: useProjectStore().phase(project.id, activity.phase).title,
          type: String,
        },
        {
          value: undefined,
          type: String,
        },
      ]);
      let numberCounter = 1;
      const documentCount =
        containsDocuments && activity.documentsIds
          ? activity.documentsIds.length
          : 0;
      const paddingLength = (
        activity.completion_criteria.length +
        activity.actions.length +
        documentCount
      ).toString().length;
      activity.completion_criteria.forEach((cc) => {
        const stateKey = `statusBadges.${cc.state}`;
        const statusKey = `statusColors.${cc.status}`;
        rows.push([
          {
            value: `${activity.number.toString()}.${numberCounter
              .toString()
              .padStart(paddingLength, '0')}`,
            type: String,
          },
          {
            value: $t('completionCriterion.abbreviation'),
            type: String,
          },
          {
            value: cc.title,
            type: String,
          },
          {
            value: cc.current_deadline.replace('T00:00Z', ''),
            type: String,
          },
          {
            value: $t(stateKey),
            type: String,
          },
          {
            value: cc.status ? $t(statusKey) : undefined,
            type: String,
          },
          {
            value: cc.responsible.user?.email || undefined,
            type: String,
          },
          {
            value:
              cc.responsible.function_id !== null
                ? useProjectStore().projectFunction(
                    project.id,
                    cc.responsible.function_id,
                  ).name
                : undefined,
            type: String,
          },
          {
            value: useProjectStore().workstream(project.id, activity.workstream)
              .title,
            type: String,
          },
          {
            value: useProjectStore().phase(project.id, activity.phase).title,
            type: String,
          },
          {
            value: undefined,
            type: String,
          },
        ]);
        numberCounter += 1;
      });
      activity.actions.forEach((action) => {
        const stateKey = `statusBadges.${action.state}`;
        const statusKey = `statusColors.${action.status}`;
        rows.push([
          {
            value: `${activity.number.toString()}.${numberCounter
              .toString()
              .padStart(paddingLength, '0')}`,
            type: String,
          },
          {
            value: $t('actions.abbreviation'),
            type: String,
          },
          {
            value: action.title,
            type: String,
          },
          {
            value: action.current_deadline.replace('T00:00Z', ''),
            type: String,
          },
          {
            value: $t(stateKey),
            type: String,
          },
          {
            value: action.status ? $t(statusKey) : undefined,
            type: String,
          },
          {
            value: action.responsible.user?.email || undefined,
            type: String,
          },
          {
            value:
              action.responsible.function_id !== null
                ? useProjectStore().projectFunction(
                    project.id,
                    action.responsible.function_id,
                  ).name
                : undefined,
            type: String,
          },
          {
            value: useProjectStore().workstream(project.id, activity.workstream)
              .title,
            type: String,
          },
          {
            value: useProjectStore().phase(project.id, activity.phase).title,
            type: String,
          },
          {
            value: undefined,
            type: String,
          },
        ]);
        numberCounter += 1;
      });
      if (containsDocuments && activity.documentsIds) {
        const activityDocuments = activity.documentsIds
          .map((docId) => project.documents.find((doc) => doc.id === docId))
          .filter((doc) => doc !== undefined);
        let documentCounter = 1;
        activityDocuments.forEach((document) => {
          rows.push([
            {
              value: `${activity.number.toString()}.D.${documentCounter
                .toString()
                .padStart(paddingLength, '0')}`,
              type: String,
            },
            {
              value: $t('files.abbreviation'),
              type: String,
            },
            {
              value: document.title,
              type: String,
            },
            {
              value: undefined,
              type: String,
            },
            {
              value: undefined,
              type: String,
            },
            {
              value: undefined,
              type: String,
            },
            {
              value: undefined,
              type: String,
            },
            {
              value: undefined,
              type: String,
            },
            {
              value: undefined,
              type: String,
            },
            {
              value: undefined,
              type: String,
            },
            {
              value: undefined,
              type: String,
            },
            {
              value: document.link
                ? `=HYPERLINK("${document.link}", "${document.link}")`
                : undefined,
              type: document.link ? 'Formula' : String,
            },
          ]);
          documentCounter += 1;
        });
      }
    });

  return writeXlsxFile(rows, {
    fileName: 'beeboard_export.xlsx',
  });
}

export async function exportTemplateExcelFile(
  template: ProjectMapTemplate,
  $t: ComposerTranslation,
) {
  const rows: SheetData = [
    [
      {
        value: $t('glossary.number'),
        type: String,
      },
      {
        value: $t('activity.position'),
        type: String,
      },
      {
        value: $t('task.type'),
        type: String,
      },
      {
        value: $t('activity.title'),
        type: String,
      },
      {
        value: $t('activity.description'),
        type: String,
      },
      {
        value: $t('function.function'),
        type: String,
      },
      {
        value: $t('glossary.workstream'),
        type: String,
      },
      {
        value: $t('glossary.phase'),
        type: String,
      },
      {
        value: $t('glossary.url'),
        type: String,
      },
    ],
  ];
  template.activities
    .sort((a, b) => (a.sequence < b.sequence ? -1 : 1))
    .forEach((activity) => {
      rows.push([
        {
          value: activity.sequence.toString().padStart(3, '0'),
          type: String,
        },
        {
          value: activity.position.toString(),
          type: String,
        },
        {
          value: $t('activity.abbreviation'),
          type: String,
        },
        {
          value: activity.title,
          type: String,
        },
        {
          value: activity.description,
          type: String,
        },
        {
          value:
            activity.responsible !== null
              ? useTemplateStore().projectFunction(
                  template.id,
                  activity.responsible,
                ).name
              : undefined,
          type: String,
        },
        {
          value: useTemplateStore().workstream(template.id, activity.workstream)
            .title,
          type: String,
        },
        {
          value: useTemplateStore().phase(template.id, activity.phase).title,
          type: String,
        },
        {
          value: undefined,
          type: String,
        },
      ]);
      let numberCounter = 1;
      const paddingLength =
        activity.completion_criteria.length.toString().length;
      activity.completion_criteria.forEach((cc) => {
        rows.push([
          {
            value: `${activity.sequence
              .toString()
              .padStart(3, '0')}.${numberCounter
              .toString()
              .padStart(paddingLength, '0')}`,
            type: String,
          },
          {
            value: activity.position.toString(),
            type: String,
          },
          {
            value: $t('completionCriterion.abbreviation'),
            type: String,
          },
          {
            value: cc.title,
            type: String,
          },
          {
            value: cc.description ? cc.description : undefined,
            type: String,
          },
          {
            value: cc.responsible
              ? useTemplateStore().projectFunction(template.id, cc.responsible)
                  .name
              : undefined,
            type: String,
          },
          {
            value: useTemplateStore().workstream(
              template.id,
              activity.workstream,
            ).title,
            type: String,
          },
          {
            value: useTemplateStore().phase(template.id, activity.phase).title,
            type: String,
          },
          {
            value: undefined,
            type: String,
          },
        ]);
        numberCounter += 1;
      });
      const activityLinks = (activity.links ?? [])
        .map((linkId) => template.links.find((link) => link.id === linkId))
        .filter((link) => link !== undefined);
      let linkCounter = 1;
      const linkPaddingLength = activityLinks.length.toString().length;
      activityLinks.forEach((link) => {
        rows.push([
          {
            value: `${activity.sequence
              .toString()
              .padStart(3, '0')}.L.${linkCounter
              .toString()
              .padStart(linkPaddingLength, '0')}`,
            type: String,
          },
          {
            value: activity.position.toString(),
            type: String,
          },
          {
            value: $t('linksAndTools.abbreviation'),
            type: String,
          },
          {
            value: link.title,
            type: String,
          },
          {
            value: undefined,
            type: String,
          },
          {
            value: undefined,
            type: String,
          },
          {
            value: undefined,
            type: String,
          },
          {
            value: undefined,
            type: String,
          },
          {
            value: link.link
              ? `=HYPERLINK("${link.link}", "${link.link}")`
              : undefined,
            type: link.link ? 'Formula' : String,
          },
        ]);
        linkCounter += 1;
      });
    });

  return writeXlsxFile(rows, {
    fileName: 'beeboard_export.xlsx',
  });
}
